import {
  localCache,
  useIsTravelApp,
  useShowPolicyBanner,
} from "@capone/common";
import { STAYS_WISHLIST } from "@capone/experiments";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  B2BSpinner,
  BackToTopButton,
  BannerSeverity,
  HotelAmenities,
  HotelCarousel,
  HotelDescription,
  HotelName,
  HotelOverview,
  HotelPriceCompareChart,
  HotelShopRoomTypePickerVariant,
  HotelShopSimilarHotels,
  HotelTripAdvisorReviews,
  Icon,
  IconName,
  MobileHotelShopRoomTypePickerPanelSkeleton,
  NotificationBanner,
  TrackingEventControlType,
  VoidWindowNotice,
  getCancellationPolicyInfo,
  savingsAmountBasedOnCheapestProduct,
  SanitizedHtml,
} from "halifax";
import queryStringParser from "query-string";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { RouteComponentProps } from "react-router";
import {
  CallState,
  CancellationPolicyEnum,
  CancellationReason,
  Lodging,
  LodgingCollectionEnum,
  SELECTED_RECOMMENDED_HOTEL,
  SelectedTravelOfferScreen,
} from "redmond";
import { StayType } from "redmond/hotels-module/interfaces";
import { v4 as uuidv4 } from "uuid";
import { config } from "../../../../api/config";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { ClientContext } from "../../../../App";
import {
  AVAILABLE,
  CACHE_HOTEL_TOKEN,
  GLOBAL_MOBILE_NAV_EXPERIMENT,
  HOTELS_CALIFORNIA_BILL_644_EXPERIMENT,
  HOTELS_SHOP_IMAGE_MODAL,
  PREMIER_COLLECTION_EXPERIMENT,
  SIMILAR_HOTELS_1A,
  SIMILAR_HOTELS_1B,
  SIMILAR_HOTELS_EXPERIMENT,
  SIMILAR_HOTELS_VARIANTS,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  TRAVEL_WALLET_OFFER_EXPERIMENT,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  useExperiments,
} from "../../../../context/experiments";
import {
  PATH_SHOP,
  PREMIER_COLLECTION_PATH_SHOP,
} from "../../../../utils/paths";
import { RewardsAccountSelection } from "../../../rewards/components";
import { parseQueryString } from "../../../search/utils/parseQueryString";
import { TravelWalletDrawer } from "../../../travel-wallet/components";
import { TravelWalletDetailsBanner } from "../../../travel-wallet/components/TravelWalletDetailsBanner";
import { WishlistToggleHeart } from "../../../wishlist/components/WishlistToggleHeart/component";
import { resetLodging } from "../../actions/actions";
import { HotelShopCallState, HotelShopStep } from "../../reducer/state";
import { goToAvailability } from "../../utils/queryStringHelpers";
import { CALIFORNIA_BILL_644_CANCELLATION_TEXT } from "../DesktopShop/textConstants";
import { HotelShopExpandedMap } from "../HotelShopExpandedMap";
import { HotelShopSmallMap } from "../HotelShopSmallMap/component";
import { MobileHotelPhotoModal } from "../MobileHotelPhotoModal";
import { MobileHotelShopRoomTypePickerPanel } from "../MobileHotelShopRoomTypePickerPanel";
import { MobileRoomPicker } from "../MobileRoomPicker";
import { MobileShopHeader } from "../MobileShopHeader";
import { ShopErrorModal } from "../ShopErrorModal";
import { MobileShopConnectorProps } from "./container";
import "./styles.scss";
import * as textConstants from "./textConstants";

export interface IMobileShopProps
  extends MobileShopConnectorProps,
    RouteComponentProps {
  roomInfoProductsType?: HotelShopRoomTypePickerVariant;
  handleReadyToRedirect?: (
    trackingEventControl?: TrackingEventControlType
  ) => void;
}

export const MobileShop = (props: IMobileShopProps) => {
  const {
    lodging,
    hotelShopCallInProgress,
    cheapestProduct,
    chosenProduct,
    hasFailed,
    hotelShopStep,
    similarHotels,
    similarHotelsAvailabilityCallState,
    selectedAccountReferenceId,
    fromDate,
    untilDate,
    history,
    similarHotelsAvailabilityTrackingProperties,
    largestValueAccount,
    viewedHotelDetailsProperties,
    searchDistanceFromShopProperties,
    adultsCount,
    children,
    roomsCount,
    petsCount,
    hotelShopCallState,
    nightCount,
    showOfferBasedOnSelectedLodging,
    roomInfoProductsType,
    handleReadyToRedirect,
    setHotelShopProgress,
    rewardsKey,
    cancellationSummary,
    isTravelApp,
    setIsTravelApp,
  } = props;

  const dispatch = useDispatch();
  const [showExpandedHotelMap, setShowExpandedHotelMap] = useState(false);
  const [openPhotoModal, setOpenPhotoModal] = useState(false);
  const [successShopSearchFromDate, setSuccessShopSearchFromDate] =
    useState<Date | null>(null);
  const [successShopSearchUntilDate, setSuccessShopSearchUntilDate] =
    useState<Date | null>(null);

  const expState = useExperiments();

  const travelWalletOffer = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_OFFER_EXPERIMENT
  );
  const isTravelWalletOfferExperiment = useMemo(
    () => travelWalletOffer === AVAILABLE,
    [travelWalletOffer]
  );

  const hotelShopImageModal = getExperimentVariant(
    expState.experiments,
    HOTELS_SHOP_IMAGE_MODAL
  );
  const isHotelShopImageModal = useMemo(
    () => hotelShopImageModal === AVAILABLE,
    [hotelShopImageModal]
  );

  const similarHotelsVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    SIMILAR_HOTELS_EXPERIMENT,
    SIMILAR_HOTELS_VARIANTS
  );

  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT
  );
  const isTravelWalletCreditsExperiment = useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment]
  );

  const premierCollectionExperiment = getExperimentVariant(
    expState.experiments,
    PREMIER_COLLECTION_EXPERIMENT
  );
  const isPremierCollectionEnabled = useMemo(
    () => premierCollectionExperiment === AVAILABLE,
    [premierCollectionExperiment]
  );

  const globalMobileNavExperimentVariant = getExperimentVariant(
    expState.experiments,
    GLOBAL_MOBILE_NAV_EXPERIMENT
  );
  const isGlobalMobileNavExperiment = React.useMemo(
    () => globalMobileNavExperimentVariant === AVAILABLE,
    [globalMobileNavExperimentVariant]
  );

  const californiaBill644Variant = getExperimentVariant(
    expState.experiments,
    HOTELS_CALIFORNIA_BILL_644_EXPERIMENT
  );
  const isCaliforniaBill644Experiment = useMemo(
    () => californiaBill644Variant === AVAILABLE,
    [californiaBill644Variant]
  );

  const showFree24HourCancel =
    isCaliforniaBill644Experiment &&
    cancellationSummary?.reasons.includes(CancellationReason.CaliforniaBill644);

  const showEarnEnhancement =
    !!largestValueAccount && !!largestValueAccount.earn.hotelsMultiplier;

  const cacheHotelTokenEnabled =
    getExperimentVariant(expState.experiments, CACHE_HOTEL_TOKEN) === AVAILABLE;

  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  const [showOfferBanner, setShowOfferBanner] = useState(false);

  const { policies } = useContext(ClientContext);
  const showPolicyBanner = useShowPolicyBanner(policies);

  useEffect(() => {
    setShowOfferBanner(
      isTravelWalletOfferExperiment && showOfferBasedOnSelectedLodging
    );
  }, [showOfferBasedOnSelectedLodging]);

  useEffect(() => {
    if (hotelShopCallState === HotelShopCallState.Success) {
      setSuccessShopSearchFromDate(fromDate);
      setSuccessShopSearchUntilDate(untilDate);
    }
  }, [hotelShopCallState]);

  useEffect(() => {
    setTimeout(() => window.scrollTo(0, 0), 100);
  }, [hotelShopStep]);

  useEffect(() => {
    dispatch(resetLodging());
  }, []);

  const parsedQueryString = useMemo(
    () => parseQueryString(history),
    [history.location.search]
  );

  const travelApp = useIsTravelApp();

  useEffect(() => {
    setIsTravelApp(travelApp.isTravelApp);
  }, [travelApp.isTravelApp]);

  useEffect(() => {
    if (travelApp.isTravelApp) {
      if (!parsedQueryString?.shopStep) {
        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set("shopStep", hotelShopStep);

        history.replace({
          ...history.location,
          search: searchParams.toString(),
        });
      } else if (
        parsedQueryString?.shopStep &&
        parsedQueryString?.shopStep !== hotelShopStep
      ) {
        setHotelShopProgress(parsedQueryString?.shopStep);
      }
    }
  }, [hotelShopStep, parsedQueryString?.shopStep, travelApp.isTravelApp]);

  const cancellationPolicyInfo = cheapestProduct?.cancellationPolicy
    ? getCancellationPolicyInfo(
        cheapestProduct?.cancellationPolicy,
        textConstants.getCfarSecondaryText
      )
    : null;

  const returnToAvailability = (
    prevSuccessHotelFromDate?: Date | null,
    prevSuccessHotelUntilDate?: Date | null
  ) => {
    goToAvailability({
      history,
      lodging,
      fromDate,
      untilDate,
      adultsCount,
      children,
      roomsCount,
      petsCount,
      prevSuccessHotelFromDate,
      prevSuccessHotelUntilDate,
    });
  };

  const renderSkeleton = () => (
    <Box className="mobile-hotel-shop-skeleton">
      <Box>
        <HotelName isSkeleton />
      </Box>
      <HotelCarousel isSkeleton />
      <Box className="section-wrapper">
        <HotelOverview isSkeleton />
        <HotelAmenities isSkeleton />
        <HotelOverview isSkeleton />
      </Box>
      <Box className="section-wrapper">
        <HotelAmenities isSkeleton />
        <HotelAmenities isSkeleton />
        <HotelOverview isSkeleton />
      </Box>
      <Box className="section-wrapper">
        <HotelName isSkeleton />
        <HotelCarousel isSkeleton />
      </Box>
      <MobileHotelShopRoomTypePickerPanelSkeleton />
    </Box>
  );

  const renderRewardsAccount = () => (
    <Box className="mobile-hotel-shop-rewards-account-contents">
      <RewardsAccountSelection className="b2b" popoverClassName="b2b" />
    </Box>
  );

  useEffect(() => {
    // removes medallia from the fixed bottom that was covering pricing
    if (document && document.getElementById("nebula_div_btn")) {
      document!.getElementById("nebula_div_btn")!.style.display = "none";
    }
    return () => {
      if (document && document.getElementById("nebula_div_btn")) {
        document!.getElementById("nebula_div_btn")!.style.display = "unset";
      }
    };
  }, [document.getElementById("nebula_div_btn")]);

  const showSimilarHotels =
    similarHotelsAvailabilityCallState === CallState.InProcess ||
    similarHotels.length > 1;

  const renderSimilarHotels = (lodging: Lodging) =>
    showSimilarHotels ? (
      <Box
        className={clsx("similar-hotels", {
          "variant-1a": similarHotelsVariant === SIMILAR_HOTELS_1A,
          "variant-1b": similarHotelsVariant === SIMILAR_HOTELS_1B,
          loading: similarHotelsAvailabilityCallState === CallState.InProcess,
        })}
      >
        {similarHotelsAvailabilityCallState === CallState.InProcess ? (
          <B2BSpinner />
        ) : (
          <>
            <Typography className="similar-hotels-header" variant="h2">
              Other similar Hotels in <strong>{lodging.lodging.city}</strong>
            </Typography>
            <HotelShopSimilarHotels
              similarHotels={
                isPremierCollectionEnabled
                  ? similarHotels
                  : similarHotels.filter(
                      (s) =>
                        s.lodgingCollection !== LodgingCollectionEnum.Premier
                    )
              }
              nightCount={dayjs(untilDate).diff(fromDate, "days")}
              rewardsKey={selectedAccountReferenceId ?? ""}
              onCTAClick={(lodgingId: string) => {
                const lodging = similarHotels.find(
                  (similarHotel) => similarHotel.lodging.id === lodgingId
                );

                trackEvent({
                  eventName: SELECTED_RECOMMENDED_HOTEL,
                  properties: {
                    ...lodging?.trackingPropertiesV2?.properties,
                    ...similarHotelsAvailabilityTrackingProperties?.properties,
                  },
                  encryptedProperties: [
                    lodging?.trackingPropertiesV2?.encryptedProperties ?? "",
                    similarHotelsAvailabilityTrackingProperties?.encryptedProperties ??
                      "",
                  ],
                });

                const queryString = history.location.search;
                const parsedQueryStringPrimitive =
                  queryStringParser.parse(queryString);

                const childrenArray = Array.isArray(
                  parsedQueryStringPrimitive.children
                )
                  ? parsedQueryStringPrimitive.children.map((age) =>
                      parseInt(age, 10)
                    )
                  : parsedQueryStringPrimitive.children
                  ? [parseInt(parsedQueryStringPrimitive.children, 10)]
                  : [];

                const hotelQueryParams = {
                  fromDate: parsedQueryStringPrimitive.fromDate as string,
                  untilDate: parsedQueryStringPrimitive.untilDate as string,
                  adultsCount: Number(parsedQueryStringPrimitive.adultsCount),
                  children: childrenArray,
                  selectedLodgingIndex: Number(
                    parsedQueryStringPrimitive.selectedLodgingIndex
                  ),
                  selectedAccountIndex: Number(
                    parsedQueryStringPrimitive.selectedAccountIndex
                  ),
                  lodgingSelection:
                    parsedQueryStringPrimitive.lodgingSelection as string,
                  recommended: true,
                  fromHotelAvailability:
                    lodging?.lodgingCollection ===
                      LodgingCollectionEnum.Premier ||
                    lodging?.lodgingCollection ===
                      LodgingCollectionEnum.Lifestyle
                      ? true
                      : undefined,
                  shopToken: cacheHotelTokenEnabled
                    ? lodging?.price?.opaqueShopRequest
                    : localCache.set(
                        uuidv4(),
                        lodging?.price?.opaqueShopRequest || ""
                      ).key,
                };

                const path =
                  lodging?.lodgingCollection === LodgingCollectionEnum.Premier
                    ? PREMIER_COLLECTION_PATH_SHOP
                    : PATH_SHOP;
                history.push(
                  `${path}?${queryStringParser.stringify({
                    lodgingId,
                    ...hotelQueryParams,
                  })}`,
                  { fromPage: location.pathname }
                );
                window.scroll(0, 0);
              }}
              isMobile
              isGlobalMobileNavExperiment={isGlobalMobileNavExperiment}
            />
          </>
        )}
      </Box>
    ) : null;

  const renderAvailability = (hotelShopStep: HotelShopStep) => {
    const { lodging: lodgingData, price } = lodging || {};
    const savings =
      price && cheapestProduct
        ? savingsAmountBasedOnCheapestProduct(price, cheapestProduct)
        : null;

    return lodging && hotelShopStep === HotelShopStep.HotelInfo ? (
      <Box className="mobile-hotel-shop-container">
        <>
          <MobileShopHeader />

          {renderRewardsAccount()}

          {isHotelShopImageModal && (
            <MobileHotelPhotoModal
              openModal={openPhotoModal}
              setOpenModal={setOpenPhotoModal}
            />
          )}

          <Box
            position="relative"
            onClick={() => {
              if (isHotelShopImageModal) {
                trackEvent({
                  eventName: "hotel_shop_image_modal",
                  properties: { ...viewedHotelDetailsProperties },
                });
                setOpenPhotoModal(true);
              }
            }}
          >
            <HotelCarousel media={lodgingData?.media} />

            {isWishlistExperiment && lodging && lodging.lodging.id && (
              <WishlistToggleHeart
                listingId={lodging?.lodging?.id}
                listingImageUrl={lodging?.lodging?.media?.[0]?.url}
                stayType={StayType.Hotels}
                lodgingCollection={lodging?.lodging?.lodgingCollection}
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                }}
              />
            )}
          </Box>
          {isPremierCollectionEnabled &&
          lodging.minNightOfStay &&
          nightCount &&
          lodging.minNightOfStay > nightCount ? (
            <NotificationBanner
              severity={BannerSeverity.WARNING}
              content={
                <Box className="pc-minimum-night-stay-warning-banner-content">
                  <Icon name={IconName.WarningAlert} />
                  <Typography
                    variant="body1"
                    className="warning-banner-text"
                  >
                    <SanitizedHtml
                      html={textConstants.getMinNightStayBannerText(
                        lodging.minNightOfStay
                      )}
                    />
                  </Typography>
                </Box>
              }
              className="pc-minimum-stay-night-warning-banner"
            />
          ) : null}
          {showOfferBanner && (
            <TravelWalletDetailsBanner
              onDismiss={() => setShowOfferBanner(false)}
              offer={lodging.bestOfferThisLodging!}
              className="mobile-hotel-shop-offer"
              screen={SelectedTravelOfferScreen.HOTEL_DETAILS}
              bannerTextType="shop"
            />
          )}

          {showFree24HourCancel && <VoidWindowNotice />}
          <Box className={clsx("section-wrapper", "overview")}>
            <HotelOverview
              lodging={lodging}
              includePricing={lodging?.price?.displayTaxesAndFeesIncluded}
              chosenProduct={chosenProduct ?? cheapestProduct}
              nightCount={nightCount ?? 0}
              rewardsKey={rewardsKey ?? undefined}
              roomsCount={roomsCount}
            />
          </Box>

          <Box className={clsx("section-wrapper", "amenities")}>
            <Typography variant="h2">Amenities</Typography>
            <HotelAmenities
              amenities={lodgingData?.amenities}
              displayCount={4}
              showAmenitiesText={`${textConstants.SHOW_ALL_AMENITIES_TEXT} (${
                lodgingData?.amenities && lodgingData?.amenities?.length - 4
              })`}
              hideAmenityIconsInTooltip
            />
          </Box>

          {price &&
            price.comparePrices.length > 0 &&
            !!price.comparePrices.find((price) => price.source !== "Expedia") &&
            cheapestProduct &&
            savings && (
              <Box className={clsx("section-wrapper", "compare")}>
                <Typography variant="h2">Compare Prices</Typography>
                <HotelPriceCompareChart
                  lodgingPrice={price}
                  siteName="Capital One"
                  cheapestProduct={cheapestProduct}
                  earnText={
                    showEarnEnhancement
                      ? textConstants.getEarnText(
                          largestValueAccount.earn.hotelsMultiplier,
                          largestValueAccount.productDisplayName,
                          largestValueAccount.rewardsBalance
                            .currencyDescription ??
                            largestValueAccount.rewardsBalance.currency
                        )
                      : undefined
                  }
                />
              </Box>
            )}

          <Box className={clsx("section-wrapper", "property")}>
            <Typography variant="h2">About the Property</Typography>
            <HotelDescription
              description={lodging?.lodging?.description}
              onMapClick={() => {
                setShowExpandedHotelMap(true);
              }}
              map={
                <HotelShopSmallMap
                  lodging={lodging}
                  setShowExpandedHotelMap={setShowExpandedHotelMap}
                  searchDistanceFromShopProperties={
                    searchDistanceFromShopProperties
                  }
                />
              }
            />
            <HotelShopExpandedMap
              isMobile
              lodging={lodging}
              showExpandedHotelMap={showExpandedHotelMap}
              setShowExpandedHotelMap={setShowExpandedHotelMap}
            />
          </Box>

          {lodgingData?.tripAdvisorReviews &&
            lodgingData?.tripAdvisorReviews?.comments?.length > 0 && (
              <Box className={clsx("section-wrapper", "customer")}>
                <Typography variant="h2">Customer Reviews</Typography>
                <HotelTripAdvisorReviews
                  lodgingData={lodgingData}
                  trim={500}
                  isMobile
                />
              </Box>
            )}

          {cancellationPolicyInfo && (
            <Box className={clsx("section-wrapper", "cancellation")}>
              <Typography variant="h2">
                {cancellationPolicyInfo.primaryText}
              </Typography>
              <Typography variant="subtitle2">
                {showFree24HourCancel &&
                cheapestProduct?.cancellationPolicy.CancellationPolicy ===
                  CancellationPolicyEnum.NonRefundable
                  ? CALIFORNIA_BILL_644_CANCELLATION_TEXT
                  : cancellationPolicyInfo.secondaryText}
              </Typography>
            </Box>
          )}

          {lodgingData?.checkInInstructions && fromDate && untilDate && (
            <Box className={clsx("section-wrapper", "check-in-instructions")}>
              <Typography variant="h2">
                {textConstants.CHECK_IN_INSTRUCTIONS_TITLE}
              </Typography>
              <SanitizedHtml
                as={Typography}
                variant="subtitle2"
                html={textConstants.formattedCheckIn(
                  fromDate,
                  lodgingData.checkInInstructions?.checkInTime
                )}
              />
              <SanitizedHtml
                as={Typography}
                variant="body2"
                html={textConstants.formattedCheckOut(
                  untilDate,
                  lodgingData.checkInInstructions?.checkOutTime
                )}
              />
            </Box>
          )}

          {isGlobalMobileNavExperiment ? (
            <BackToTopButton bottom={110} />
          ) : undefined}

          {!isGlobalMobileNavExperiment && isTravelWalletCreditsExperiment ? (
            <TravelWalletDrawer />
          ) : undefined}

          <MobileHotelShopRoomTypePickerPanel
            showPolicyBanner={showPolicyBanner}
          />
        </>
      </Box>
    ) : (
      <></>
    );
  };

  const renderShop = (hotelShopStep: HotelShopStep) => (
    <Box
      className={clsx("mobile-hotel-shop-container", config.TENANT, {
        "travel-app": isTravelApp,
      })}
    >
      {lodging && hotelShopStep === HotelShopStep.HotelInfo ? (
        <>
          {[SIMILAR_HOTELS_1A, SIMILAR_HOTELS_1B].includes(
            similarHotelsVariant
          ) && renderSimilarHotels(lodging)}

          {isGlobalMobileNavExperiment ? (
            <BackToTopButton bottom={110} />
          ) : undefined}
        </>
      ) : (
        lodging && (
          <MobileRoomPicker
            handleReadyToRedirect={handleReadyToRedirect}
            roomInfoProductsType={roomInfoProductsType}
            nonRefundablePolicyOverrideText={
              showFree24HourCancel
                ? CALIFORNIA_BILL_644_CANCELLATION_TEXT
                : undefined
            }
          />
        )
      )}
    </Box>
  );

  return !hasFailed ? (
    <Box
      className={clsx("mobile-hotel-shop-wrapper", config.TENANT, {
        "global-mobile-nav": isGlobalMobileNavExperiment,
        "travel-app": isTravelApp,
      })}
    >
      {renderAvailability(hotelShopStep)}
      {hotelShopCallInProgress ? renderSkeleton() : renderShop(hotelShopStep)}
    </Box>
  ) : (
    <ShopErrorModal
      returnToAvailability={() =>
        returnToAvailability(
          successShopSearchFromDate,
          successShopSearchUntilDate
        )
      }
    />
  );
};
